import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, Trash2, Search, X } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const CATS = ['english','maths','logical','reading'];
const DIFFS = ['easy','medium','hard'];
const OPTS  = ['A','B','C','D'];

const EMPTY_Q = {
  category: 'english', difficulty: 'medium', question_text: '',
  passage: '', sub_category: '',
  option_a: '', option_b: '', option_c: '', option_d: '',
  correct_answer: 'A', is_active: true,
};

export default function AdminAssessmentQuestions() {
  const [questions, setQuestions] = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(false);

  const [filterCat,  setFilterCat]  = useState('');
  const [filterDiff, setFilterDiff] = useState('');
  const [filterAct,  setFilterAct]  = useState('');
  const [search,     setSearch]     = useState('');

  const [modal, setModal]     = useState(null); // null | 'add' | 'edit'
  const [form,  setForm]      = useState(EMPTY_Q);
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [formErr, setFormErr] = useState('');

  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (filterCat)  params.set('category',   filterCat);
      if (filterDiff) params.set('difficulty',  filterDiff);
      if (filterAct)  params.set('is_active',   filterAct);
      if (search)     params.set('search',      search);
      const res = await axios.get(`${API}/api/admin/assessment/questions?${params}`);
      setQuestions(res.data.questions);
      setTotal(res.data.total);
    } catch {
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [page, filterCat, filterDiff, filterAct, search]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(EMPTY_Q); setFormErr(''); setModal('add'); };
  const openEdit = (q) => {
    setForm({
      id: q.id,
      category:       q.category,
      difficulty:     q.difficulty || 'medium',
      question_text:  q.question_text,
      passage:        q.passage || '',
      sub_category:   q.sub_category || '',
      option_a:       q.options?.A || '',
      option_b:       q.options?.B || '',
      option_c:       q.options?.C || '',
      option_d:       q.options?.D || '',
      correct_answer: q.correct_answer,
      is_active:      q.is_active !== false,
    });
    setFormErr('');
    setModal('edit');
  };

  const saveQuestion = async () => {
    if (!form.question_text.trim()) { setFormErr('Question text is required.'); return; }
    if (!form.option_a.trim() || !form.option_b.trim() || !form.option_c.trim() || !form.option_d.trim()) {
      setFormErr('All 4 options are required.'); return;
    }
    setSaving(true); setFormErr('');
    try {
      const payload = {
        category:       form.category,
        difficulty:     form.difficulty,
        question_text:  form.question_text,
        passage:        form.passage || null,
        sub_category:   form.sub_category || null,
        options:        { A: form.option_a, B: form.option_b, C: form.option_c, D: form.option_d },
        correct_answer: form.correct_answer,
        is_active:      form.is_active,
      };
      if (modal === 'edit') {
        await axios.put(`${API}/api/admin/assessment/questions/${form.id}`, payload);
      } else {
        await axios.post(`${API}/api/admin/assessment/questions`, payload);
      }
      setModal(null);
      load();
    } catch (err) {
      setFormErr(err.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (q) => {
    try {
      await axios.patch(`${API}/api/admin/assessment/questions/${q.id}/toggle`);
      load();
    } catch { /* ignore */ }
  };

  const deleteQ = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/api/admin/assessment/questions/${id}`);
      load();
    } catch { /* ignore */ }
    finally { setDeleting(null); }
  };

  const pages = Math.ceil(total / LIMIT);

  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Question Bank</h1>
          <p className="text-sm text-slate-500">{total} total questions</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 h-9 rounded-lg text-white text-sm font-medium hover:brightness-110 transition"
          style={{ backgroundColor: '#1e73be' }}>
          <Plus className="w-4 h-4" /> Add Question
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search questions…"
            className="w-full h-10 pl-9 pr-8 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1e73be]/30" />
          {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-slate-400" /></button>}
        </div>
        {[
          { val: filterCat,  set: v => { setFilterCat(v); setPage(1); },  opts: CATS,  ph: 'All Categories' },
          { val: filterDiff, set: v => { setFilterDiff(v); setPage(1); }, opts: DIFFS, ph: 'All Difficulties' },
          { val: filterAct,  set: v => { setFilterAct(v); setPage(1); },  opts: [['true','Active'],['false','Inactive']], ph: 'All Status' },
        ].map(({ val, set, opts, ph }, i) => (
          <select key={i} value={val} onChange={e => set(e.target.value)}
            className="h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white outline-none">
            <option value="">{ph}</option>
            {opts.map(o => Array.isArray(o)
              ? <option key={o[0]} value={o[0]}>{o[1]}</option>
              : <option key={o} value={o}>{o.charAt(0).toUpperCase()+o.slice(1)}</option>
            )}
          </select>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-slate-400">Loading…</div>
        ) : questions.length === 0 ? (
          <div className="py-12 text-center text-slate-400">No questions found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-[40%]">Question</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Difficulty</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {questions.map(q => (
                  <tr key={q.id} className="hover:bg-slate-50 transition">
                    <td className="px-4 py-3">
                      <p className="text-slate-800 line-clamp-2 text-sm">{q.question_text}</p>
                      {q.passage && <span className="text-[10px] text-amber-600 mt-0.5 inline-block">📖 Has passage</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 rounded-full font-medium"
                        style={{ backgroundColor: '#eaf3fb', color: '#1e73be' }}>
                        {q.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium
                        ${q.difficulty==='easy'   ? 'bg-green-50 text-green-700' :
                          q.difficulty==='medium' ? 'bg-amber-50 text-amber-700' :
                                                    'bg-red-50 text-red-600'}`}>
                        {q.difficulty || 'medium'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleActive(q)}
                        className={`text-xs px-2.5 py-1 rounded-full font-medium transition ${
                          q.is_active !== false
                            ? 'bg-green-50 text-green-700 hover:bg-green-100'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}>
                        {q.is_active !== false ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEdit(q)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#1e73be] transition">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => window.confirm('Delete this question?') && deleteQ(q.id)}
                          disabled={deleting === q.id}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition disabled:opacity-50">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex justify-end gap-2">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
            className="px-3 h-9 rounded-lg border border-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50">Prev</button>
          <span className="px-3 h-9 flex items-center text-sm text-slate-500">{page}/{pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p+1))} disabled={page===pages}
            className="px-3 h-9 rounded-lg border border-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50">Next</button>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-semibold text-slate-800">{modal==='add' ? 'Add New Question' : 'Edit Question'}</h3>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={lbl}>Category</label>
                  <select value={form.category} onChange={e => sf('category', e.target.value)} className={sel}>
                    {CATS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Difficulty</label>
                  <select value={form.difficulty} onChange={e => sf('difficulty', e.target.value)} className={sel}>
                    {DIFFS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase()+d.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Correct Answer</label>
                  <select value={form.correct_answer} onChange={e => sf('correct_answer', e.target.value)} className={sel}>
                    {OPTS.map(o => <option key={o} value={o}>Option {o}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={lbl}>Sub-category (optional)</label>
                <input value={form.sub_category} onChange={e => sf('sub_category', e.target.value)}
                  placeholder="e.g. Passage 1, Arithmetic" className={inp} />
              </div>

              <div>
                <label className={lbl}>Passage (optional — for Reading Comprehension)</label>
                <textarea value={form.passage} onChange={e => sf('passage', e.target.value)}
                  rows={3} placeholder="Paste the passage text here…" className={inp + ' py-2 resize-none'} />
              </div>

              <div>
                <label className={lbl}>Question Text <span className="text-red-500">*</span></label>
                <textarea value={form.question_text} onChange={e => sf('question_text', e.target.value)}
                  rows={3} placeholder="Enter the question…" className={inp + ' py-2 resize-none'} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {OPTS.map(o => (
                  <div key={o}>
                    <label className={lbl}>Option {o} {o==='A'||o==='B' ? <span className="text-red-500">*</span> : ''}</label>
                    <input value={form[`option_${o.toLowerCase()}`]}
                      onChange={e => sf(`option_${o.toLowerCase()}`, e.target.value)}
                      placeholder={`Option ${o}`} className={inp} />
                  </div>
                ))}
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.is_active} onChange={e => sf('is_active', e.target.checked)}
                  className="accent-[#1e73be]" />
                Mark as active (will appear in tests)
              </label>

              {formErr && <p className="text-sm text-red-600 bg-red-50 p-2 rounded-lg">{formErr}</p>}
            </div>
            <div className="flex gap-3 p-5 border-t bg-slate-50 rounded-b-2xl">
              <button onClick={() => setModal(null)}
                className="flex-1 h-11 rounded-xl border border-slate-300 text-slate-700 font-medium text-sm hover:bg-white transition">
                Cancel
              </button>
              <button onClick={saveQuestion} disabled={saving}
                className="flex-1 h-11 rounded-xl text-white font-semibold text-sm hover:brightness-110 transition disabled:opacity-60"
                style={{ backgroundColor: '#1e73be' }}>
                {saving ? 'Saving…' : modal==='add' ? 'Add Question' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const lbl = 'block text-xs font-medium text-slate-600 mb-1';
const inp = 'w-full px-3 h-10 border border-slate-300 rounded-lg outline-none text-sm focus:ring-2 focus:ring-[#1e73be]/30 focus:border-[#1e73be]';
const sel = inp + ' bg-white';
